export type PageLoadAutomationSignal = "controlled" | "none" | "webdriver";
export type PageLoadMeasurementKind = "page_load" | "route_transition";
export type PageLoadReadinessSignal =
  | "document_ready"
  | "list_content_ready"
  | "load_event"
  | "profile_overview_ready"
  | "profile_shell_ready"
  | "route_committed";
export type PageLoadTrafficClass = "automated" | "human" | "unknown";
export type PageLoadAutomationReason = "controlled" | "none" | "unclassified" | "user_agent" | "webdriver";

type NavigationTiming = Pick<PerformanceNavigationTiming, "domContentLoadedEventEnd" | "startTime">;

const automatedUserAgentPattern = /bot|crawler|facebookexternalhit|headlesschrome|lighthouse|pagespeed|playwright|puppeteer|selenium|spider|synthetic|uptimerobot/i;

type MeasurementIdCrypto = Pick<Crypto, "getRandomValues"> & Partial<Pick<Crypto, "randomUUID">>;

export function createPageLoadMeasurementId(cryptoApi: MeasurementIdCrypto): string {
  if (typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0F) | 0x40;
  bytes[8] = (bytes[8]! & 0x3F) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getHardNavigationReadyDurationMs(
  entry: NavigationTiming | null,
  readyAtMs: number,
): number {
  const navigationStart = entry?.startTime ?? 0;
  const documentReadyAt = entry && entry.domContentLoadedEventEnd > 0
    ? entry.domContentLoadedEventEnd
    : readyAtMs;

  return Math.max(documentReadyAt, readyAtMs) - navigationStart;
}

export function getPageLoadAutomationSignal(webdriver: boolean): PageLoadAutomationSignal {
  return webdriver ? "webdriver" : "none";
}

export function classifyPageLoadTraffic({
  automationSignal,
  userAgent,
}: {
  automationSignal?: PageLoadAutomationSignal;
  userAgent: string | null;
}): {
  automationReason: PageLoadAutomationReason;
  trafficClass: PageLoadTrafficClass;
} {
  if (automationSignal === "webdriver") {
    return { automationReason: "webdriver", trafficClass: "automated" };
  }

  if (automationSignal === "controlled") {
    return { automationReason: "controlled", trafficClass: "automated" };
  }

  if (userAgent && automatedUserAgentPattern.test(userAgent)) {
    return { automationReason: "user_agent", trafficClass: "automated" };
  }

  if (automationSignal === "none" || userAgent) {
    return { automationReason: "none", trafficClass: "human" };
  }

  return { automationReason: "unclassified", trafficClass: "unknown" };
}
